import { create } from 'zustand';
import type { ShotSettingsPayloadData, ShotDeletedPayload, ShotsDeletedPayload, ShotMutatedPayload } from '@/bridge/features/shotSettings';

interface ShotSettingsState {
  current: ShotSettingsPayloadData | null;
  /** Fresh object per shotDeleted echo (object-identity ack, same trick as `current`). */
  deleteResult: ShotDeletedPayload | null;
  /** Fresh object per shotsDeleted echo — the BATCH delete's ack (board select mode, 20260909). A
   *  SEPARATE slice from deleteResult on purpose: the two acks have different shapes and different
   *  waiters, and a batch delete must never resolve a single delete's gate (or the reverse). */
  deleteManyResult: ShotsDeletedPayload | null;
  /** Fresh object per shotMutated echo — the board context menu's copy/insert/paste ack. */
  mutateResult: ShotMutatedPayload | null;
  /** shotGradePreviewKey(shotId, postProcessingKey, sceneFloorIsTransparent) -> data-URL of Unity's
   *  render of that shot with those candidate settings, populated by the setShotGradePreview bridge
   *  handler. Dropped by clear() with everything else: these are full-frame JPEGs of one shot,
   *  nothing outside the open sheet reads them, and settings applied on Done make them stale anyway
   *  (the board tile re-renders and becomes the truth again). */
  gradePreviews: Record<string, string>;
  setCurrent: (d: ShotSettingsPayloadData) => void;
  setDeleteResult: (d: ShotDeletedPayload) => void;
  clearDeleteResult: () => void;
  setDeleteManyResult: (d: ShotsDeletedPayload) => void;
  clearDeleteManyResult: () => void;
  setMutateResult: (d: ShotMutatedPayload) => void;
  clearMutateResult: () => void;
  clear: () => void;
}

export const useShotSettingsStore = create<ShotSettingsState>((set) => ({
  current: null,
  deleteResult: null,
  deleteManyResult: null,
  mutateResult: null,
  gradePreviews: {},
  setCurrent: (d) => set({ current: d }),
  setDeleteResult: (d) => set({ deleteResult: d }),
  clearDeleteResult: () => set({ deleteResult: null }),
  setDeleteManyResult: (d) => set({ deleteManyResult: d }),
  clearDeleteManyResult: () => set({ deleteManyResult: null }),
  setMutateResult: (d) => set({ mutateResult: d }),
  clearMutateResult: () => set({ mutateResult: null }),
  clear: () => set({ current: null, deleteResult: null, deleteManyResult: null, mutateResult: null, gradePreviews: {} }),
}));
